import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import api from '../services/api'
import { useModal } from '../components/Modal'

export default function EmployeesPage() {
  const navigate = useNavigate()
  const { showAlert, showConfirm, ModalComponents } = useModal()
  const [registrationSummary, setRegistrationSummary] = useState({ data: [], loading: true, error: '' })
  const summaryRequestRef = useRef(0)
  const fetchRegistrationSummary = useCallback(async () => {
    const requestId = ++summaryRequestRef.current
    setRegistrationSummary(previous => ({ ...previous, loading: true, error: '' }))
    try {
      const res = await api.get('/registration/summary-by-division')
      if (!res.data?.success || !Array.isArray(res.data.data)) throw new Error('Invalid summary response')
      const rows = res.data.data.map(row => {
        const count = Number(row.registration_count)
        if (row.registration_count === null || row.registration_count === '' || !Number.isSafeInteger(count) || count < 0) throw new Error('Invalid registration count')
        return { ...row, registration_count: count }
      })
      if (requestId === summaryRequestRef.current) setRegistrationSummary({ data: rows, loading: false, error: '' })
    } catch (_) {
      if (requestId === summaryRequestRef.current) setRegistrationSummary(previous => ({ ...previous, loading: false, error: 'โหลดจำนวนผู้ลงทะเบียนไม่สำเร็จ กรุณาลองใหม่' }))
    }
  }, [])

  useEffect(() => {
    fetchRegistrationSummary()
    const interval = setInterval(fetchRegistrationSummary, 60000)
    window.addEventListener('notifications:refresh', fetchRegistrationSummary)
    return () => {
      summaryRequestRef.current += 1
      clearInterval(interval)
      window.removeEventListener('notifications:refresh', fetchRegistrationSummary)
    }
  }, [fetchRegistrationSummary])

  const [employees, setEmployees] = useState([])
  const [divisions, setDivisions] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterDivision, setFilterDivision] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('active')
  const [showModal, setShowModal] = useState(false)
  const [editData, setEditData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [registrations, setRegistrations] = useState([])
  const [registrationSearch, setRegistrationSearch] = useState('')
  const [registrationDivision, setRegistrationDivision] = useState('')
  const registrationTerms = registrationSearch.normalize('NFC').trim().toLocaleLowerCase('th').split(/\s+/).filter(Boolean)
  const filteredRegistrations = registrations.filter(reg => {
    const name = `${reg.prefix || ''}${reg.first_name || ''} ${reg.last_name || ''}`.normalize('NFC').toLocaleLowerCase('th')
    const matchesDivision = !registrationDivision || String(reg.division_id || '') === registrationDivision
    return matchesDivision && registrationTerms.every(term => name.includes(term))
  })
  const [loadingReg, setLoadingReg] = useState(false)
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('tab') === 'registrations' ? 'registrations' : 'employees'
  })
  const [rejectReason,setRejectReason] = useState('')
  const [selectedReg, setSelectedReg] = useState(null)
  const [showRegModal, setShowRegModal] = useState(false)

  const [form, setForm] = useState({
    id_card: '', prefix: 'นาย', first_name: '', last_name: '',
    division_id: '', position: '', emp_type: 'ทั่วไป',
    phone: '', email: '', birth_date: '',
    house_no: '', village_no: '', road: '',
    subdistrict: '', district: '', province: '', zipcode: '', address: ''
  })

  useEffect(() => {
    fetchEmployees()
    fetchDivisions()
    fetchRegistrations()
    const interval = setInterval(fetchRegistrations, 15000)
    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterDivision, filterType, filterStatus])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('tab') === 'registrations') setActiveTab('registrations')
  }, [])

  const fetchEmployees = async () => {
    try {
      setLoading(true)
      const params = {}
      if (search) params.search = search
      if (filterDivision) params.division_id = filterDivision
      if (filterType) params.emp_type = filterType
      if (filterStatus) params.status = filterStatus
      const res = await api.get('/employees', { params })
      if (res.data.success) setEmployees(res.data.data)
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const fetchDivisions = async () => {
    try {
      const res = await api.get('/divisions')
      if (res.data.success) setDivisions(res.data.data)
    } catch (err) { console.error(err) }
  }

  const fetchRegistrations = async () => {
    try {
      setLoadingReg(true)
      const res = await api.get('/registration/pending')
      if (res.data.success) setRegistrations(res.data.data)
    } catch (err) { console.error(err) }
    finally { setLoadingReg(false) }
  }

  const handleApproveReg = async (reg) => {
    const ok = await showConfirm('success', 'อนุมัติการลงทะเบียน',
      `อนุมัติ ${reg.prefix || ''}${reg.first_name} ${reg.last_name} และเพิ่มเข้าระบบพนักงาน ใช่หรือไม่?`)
    if (!ok) return
    try {
      await api.patch(`/registration/${reg.id}/approve`, { action: 'approve' })
      showAlert('success', 'อนุมัติสำเร็จ', `เพิ่ม ${reg.first_name} ${reg.last_name} เข้าระบบแล้ว`)
      setShowRegModal(false)
      fetchRegistrations()
      fetchEmployees()
      window.dispatchEvent(new Event('notifications:refresh'))
    } catch (err) {
      showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด')
    }
  }

  const handleRejectReg = async (reg) => {
    if (!rejectReason.trim()) { showAlert('warning','กรุณาระบุเหตุผล','กรอกเหตุผลไม่อนุมัติก่อนยืนยัน'); return }
    const ok = await showConfirm('danger', 'ไม่อนุมัติการลงทะเบียน',
      `ไม่อนุมัติคำขอของ ${reg.prefix || ''}${reg.first_name} ${reg.last_name} ใช่หรือไม่?`)
    if (!ok) return
    try {
      await api.patch(`/registration/${reg.id}/approve`, { action: 'reject', note: rejectReason.trim() })
      showAlert('info', 'ไม่อนุมัติแล้ว', 'บันทึกคำขอส่งอีเมลพร้อมเหตุผลแล้ว ระบบกำลังส่งให้พนักงาน')
      setShowRegModal(false)
      fetchRegistrations()
      window.dispatchEvent(new Event('notifications:refresh'))
    } catch (err) {
      showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด')
    }
  }

  const openAddModal = () => {
    setEditData(null)
    setForm({ id_card: '', prefix: 'นาย', first_name: '', last_name: '', division_id: '', position: '', emp_type: 'ทั่วไป', phone: '', email: '', birth_date: '', house_no: '', village_no: '', road: '', subdistrict: '', district: '', province: '', zipcode: '', address: '' })
    setError('')
    setShowModal(true)
  }

  const openEditModal = (emp) => {
    setEditData(emp)
    setForm({
      salary: emp.salary ?? '', note: '',
      id_card: emp.id_card || '', prefix: emp.prefix || 'นาย',
      first_name: emp.first_name || '', last_name: emp.last_name || '',
      division_id: emp.division_id || '', position: emp.position || '',
      emp_type: emp.emp_type || 'ทั่วไป', phone: emp.phone || '',
      email: emp.email || '', birth_date: emp.birth_date ? emp.birth_date.split('T')[0] : '',
      house_no: emp.house_no || '', village_no: emp.village_no || '',
      road: emp.road || '', subdistrict: emp.subdistrict || '',
      district: emp.district || '', province: emp.province || '',
      zipcode: emp.zipcode || '', address: emp.address || ''
    })
    setError('')
    setShowModal(true)
  }

  const handleSave = async () => {
    setError('')
    if (!form.id_card || form.id_card.length !== 13) { setError('กรุณากรอกเลขบัตรประชาชน 13 หลัก'); return }
    if (!form.first_name) { setError('กรุณากรอกชื่อ'); return }
    if (!form.last_name) { setError('กรุณากรอกนามสกุล'); return }
    if (!editData && !form.birth_date) { setError('กรุณากรอกวันเกิด'); return }
    if (!form.division_id) { setError('กรุณาเลือกกองงาน'); return }
    try {
      setSaving(true)
      const fullAddress = [form.house_no, form.village_no ? `หมู่ ${form.village_no}` : '', form.road ? `ถนน${form.road}` : '', form.subdistrict ? `ต.${form.subdistrict}` : '', form.district ? `อ.${form.district}` : '', form.province ? `จ.${form.province}` : '', form.zipcode].filter(Boolean).join(' ')
      const payload = { ...form, address: fullAddress }
      if (editData) await api.put(`/employees/${editData.id}`, {division_id:form.division_id,position:form.position,salary:form.salary,note:form.note})
      else await api.post('/employees', payload)
      setShowModal(false)
      fetchEmployees()
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด')
    } finally { setSaving(false) }
  }

  const calcAge = (birthDate) => {
    if (!birthDate) return '-'
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
    return `${age} ปี`
  }

  const empTypeConfig = {
    'ทั่วไป': { color: '#3b82f6', bg: '#eff6ff' },
    'ตามภารกิจ': { color: '#8b5cf6', bg: '#f5f3ff' },
    'ผู้เชี่ยวชาญพิเศษ': { color: '#f97316', bg: '#fff7ed' },
  }

  const inputStyle = { width: '100%', padding: '8px 12px', border: '0.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#0f172a', background: 'white', outline: 'none', fontFamily: 'inherit' }

  return (
    <Layout>
      <ModalComponents />
      <div className="ice-list-page" style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '500', color: '#0f172a' }}>จัดการพนักงาน</h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>พนักงานทั้งหมดในระบบ</p>
          </div>
          <button onClick={openAddModal}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            <i className="ti ti-user-plus" style={{ fontSize: '16px' }} />
            เพิ่มพนักงาน
          </button>
        </div>

        <section aria-label="จำนวนผู้ลงทะเบียนแยกตามกองงาน" style={{ background: 'white', borderRadius: '14px', border: '1px solid #dbeafe', marginBottom: '20px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', color: '#075dcc', fontWeight: '600' }}>จำนวนผู้ลงทะเบียนแยกตามกองงาน</h2>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>รวมทุกสถานะการลงทะเบียน ไม่รวมรายการที่ลบแล้ว</p>
            </div>
            <button type="button" onClick={fetchRegistrationSummary} disabled={registrationSummary.loading}
              style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#075dcc', cursor: registrationSummary.loading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              {registrationSummary.loading ? 'กำลังโหลดจำนวน…' : 'โหลดจำนวนใหม่'}
            </button>
          </div>
          {registrationSummary.loading ? <p role="status" style={{ padding: '0 16px 14px', color: '#64748b' }}>กำลังโหลดจำนวนผู้ลงทะเบียน…</p>
            : registrationSummary.error ? <p role="alert" style={{ padding: '0 16px 14px', color: '#b45309' }}>{registrationSummary.error}</p>
            : registrationSummary.data.length === 0 ? <p style={{ padding: '0 16px 14px', color: '#64748b' }}>ยังไม่มีข้อมูลกองงานหรือผู้ลงทะเบียน</p>
            : <div style={{ maxHeight: '280px', overflow: 'auto' }}>
              <table aria-label="ยอดลงทะเบียนแต่ละกองงาน" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#e0f2fe', color: '#0c4a6e' }}>
                  <th scope="col" style={{ textAlign: 'left', padding: '10px 16px', position: 'sticky', top: 0, background: '#e0f2fe' }}>กองงาน</th>
                  <th scope="col" style={{ textAlign: 'right', padding: '10px 16px', position: 'sticky', top: 0, background: '#e0f2fe', whiteSpace: 'nowrap' }}>ลงทะเบียนแล้ว (คน)</th>
                </tr></thead>
                <tbody>{registrationSummary.data.map(row => <tr key={row.division_id || 'unassigned'} style={{ borderTop: '1px solid #e0f2fe' }}>
                  <th scope="row" style={{ textAlign: 'left', padding: '10px 16px', fontSize: '13px', fontWeight: '400', color: '#334155' }}>{row.division_name}</th>
                  <td style={{ textAlign: 'right', padding: '10px 16px', fontSize: '15px', fontWeight: '600', color: '#075dcc' }}>{row.registration_count.toLocaleString('th-TH')}</td>
                </tr>)}</tbody>
              </table>
            </div>}
        </section>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button onClick={() => setActiveTab('employees')}
            style={{ padding: '8px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', background: activeTab === 'employees' ? '#075dcc' : 'white', color: activeTab === 'employees' ? 'white' : '#64748b', border: activeTab === 'employees' ? 'none' : '0.5px solid #e2e8f0' }}>
            พนักงานทั้งหมด
          </button>
          <button onClick={() => setActiveTab('registrations')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', background: activeTab === 'registrations' ? '#f97316' : 'white', color: activeTab === 'registrations' ? 'white' : '#64748b', border: activeTab === 'registrations' ? 'none' : '0.5px solid #e2e8f0' }}>
            รอตรวจสอบ
            {registrations.length > 0 && (
              <span style={{ background: activeTab === 'registrations' ? 'white' : '#f97316', color: activeTab === 'registrations' ? '#f97316' : 'white', fontSize: '10px', padding: '1px 7px', borderRadius: '999px', fontWeight: '500' }}>
                {registrations.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab: พนักงาน */}
        {activeTab === 'employees' && (
          <>
            <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', padding: '16px', marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
                <i className="ti ti-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#94a3b8' }} />
                <input type="text" placeholder="ค้นหาชื่อ นามสกุล เลขบัตร..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: '34px' }} />
              </div>
              <select value={filterDivision} onChange={e => setFilterDivision(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                <option value="">กองงานทั้งหมด</option>
                {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                <option value="">ทั้งหมด</option>
                <option value="active">ใช้งาน</option>
                <option value="inactive">ปิดใช้งาน</option>
              </select>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
                <option value="">ประเภททั้งหมด</option>
                <option value="ทั่วไป">ทั่วไป</option>
                <option value="ตามภารกิจ">ตามภารกิจ</option>
                <option value="ผู้เชี่ยวชาญพิเศษ">ผู้เชี่ยวชาญพิเศษ</option>
              </select>
            </div>

            <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', overflow: 'hidden' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontSize: '13px' }}>กำลังโหลด...</div>
              ) : employees.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontSize: '13px' }}>ไม่พบข้อมูลพนักงาน</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        {['ชื่อ-นามสกุล', 'เลขบัตร', 'กองงาน', 'ตำแหน่ง', 'ประเภท', 'สถานะ', 'จัดการ'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => {
                        const tc = empTypeConfig[emp.emp_type] || { color: '#64748b', bg: '#f1f5f9' }
                        return (
                          <tr key={emp.id} style={{ borderTop: '0.5px solid #f1f5f9' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{emp.prefix}{emp.first_name} {emp.last_name}</td>
                            <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94a3b8' }}>{emp.id_card}</td>
                            <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{emp.division_name || '-'}</td>
                            <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{emp.position || '-'}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', color: tc.color, background: tc.bg }}>{emp.emp_type || '-'}</span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', color: emp.status === 'active' ? '#22c55e' : '#94a3b8', background: emp.status === 'active' ? '#f0fdf4' : '#f8fafc' }}>
                                {emp.status === 'active' ? 'ใช้งาน' : 'ปิดใช้งาน'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={() => openEditModal(emp)}
                                  style={{ padding: '4px 10px', background: '#eff6ff', color: '#075dcc', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>แก้ไข</button>
                                <button onClick={() => navigate(`/employees/${emp.id}/history`)}
                                  style={{ padding: '4px 10px', background: '#f5f3ff', color: '#8b5cf6', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>ประวัติ</button>

                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Tab: รอตรวจสอบ */}
        {activeTab === 'registrations' && (
          <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 260px', maxWidth: '420px', minWidth: 0 }}>
                  <label htmlFor="registration-name-search" style={{ display: 'block', marginBottom: '6px' }}>ค้นหารายชื่อผู้ลงทะเบียนรอตรวจสอบ</label>
                  <input id="registration-name-search" type="search" value={registrationSearch}
                    onChange={e => setRegistrationSearch(e.target.value)} placeholder="พิมพ์ชื่อหรือนามสกุล"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px' }} />
                </div>
                <div style={{ flex: '1 1 240px', maxWidth: '360px', minWidth: 0 }}>
                  <label htmlFor="registration-division-filter" style={{ display: 'block', marginBottom: '6px' }}>เลือกกองงาน</label>
                  <select id="registration-division-filter" value={registrationDivision}
                    onChange={e => setRegistrationDivision(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white' }}>
                    <option value="">ทุกกองงาน</option>
                    {divisions.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                  </select>
                </div>
                {(registrationSearch || registrationDivision) && <button type="button"
                  onClick={() => { setRegistrationSearch(''); setRegistrationDivision('') }}
                  style={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white', cursor: 'pointer' }}>
                  ล้างตัวกรอง
                </button>}
              </div>
              <p role="status" style={{ marginTop: '8px', color: '#64748b', fontSize: '12px' }}>พบ {filteredRegistrations.length} จาก {registrations.length} รายการรอตรวจสอบ</p>
            </div>
            {loadingReg ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontSize: '13px' }}>กำลังโหลด...</div>
            ) : filteredRegistrations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <i className="ti ti-circle-check" style={{ fontSize: '48px', color: '#22c55e', display: 'block', marginBottom: '12px' }} />
                <p style={{ color: '#94a3b8', fontSize: '13px' }}>{registrations.length ? 'ไม่พบรายการที่ตรงกับคำค้นหรือกองงานที่เลือก' : 'ไม่มีรายการรอตรวจสอบ'}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fff7ed' }}>
                      {['ชื่อ-นามสกุล', 'เลขบัตร', 'กองงาน', 'ตำแหน่ง', 'โทร', 'วันที่ส่ง', 'จัดการ'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRegistrations.map(reg => (
                      <tr key={reg.id} style={{ borderTop: '0.5px solid #f1f5f9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{reg.prefix || ''}{reg.first_name} {reg.last_name}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94a3b8' }}>{reg.id_card}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{reg.division_name || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{reg.position || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{reg.phone || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94a3b8' }}>{new Date(reg.created_at).toLocaleDateString('th-TH')}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => { setRejectReason(''); setSelectedReg(reg); setShowRegModal(true) }}
                            style={{ padding: '5px 12px', background: '#f97316', color: 'white', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                            ตรวจสอบ
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal ตรวจสอบการลงทะเบียน */}
      {showRegModal && selectedReg && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '0.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a' }}>ตรวจสอบการลงทะเบียน</p>
                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>รอการอนุมัติจาก HR</p>
              </div>
              <button onClick={() => setShowRegModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {[
                { title: 'ข้อมูลส่วนตัว', fields: [
                  ['ชื่อ-นามสกุล', `${selectedReg.prefix || ''}${selectedReg.first_name} ${selectedReg.last_name}`],
                  ['เลขบัตร', selectedReg.id_card],
                  ['วันเกิด', selectedReg.birth_date ? new Date(selectedReg.birth_date).toLocaleDateString('th-TH') : '-'],
                  ['อายุ', calcAge(selectedReg.birth_date)],
                  ['โทรศัพท์', selectedReg.phone || '-'],
                  ['Email', selectedReg.email || '-'],
                ]},
                { title: 'ข้อมูลการทำงาน', fields: [
                  ['ตำแหน่ง', selectedReg.position || '-'],
                  ['ประเภท', selectedReg.emp_type || '-'],
                  ['กองงาน', selectedReg.division_name || '-'],
                ]},
              ].map((section, si) => (
                <div key={si} style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '10px', fontWeight: '500', color: '#94a3b8', marginBottom: '12px', textTransform: 'uppercase' }}>{section.title}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {section.fields.map(([label, value]) => (
                      <div key={label}>
                        <p style={{ fontSize: '11px', color: '#94a3b8' }}>{label}</p>
                        <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a', marginTop: '2px' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#334155' }}>เหตุผลไม่อนุมัติ
                  <textarea aria-label="เหตุผลไม่อนุมัติ" maxLength={2000} rows={3} value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="กรอกเหตุผลเมื่อเลือกไม่อนุมัติ" style={{...inputStyle, width:'100%', marginTop:'6px', resize:'vertical'}} />
                </label>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'14px' }}>
                  <button onClick={() => handleRejectReg(selectedReg)}
                    style={{ minWidth:'130px', padding:'11px 18px', background:'#fff7f7', color:'#dc2626', border:'1px solid #fca5a5', borderRadius:'10px', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
                    <i className="ti ti-x" /> ไม่อนุมัติ
                  </button>
                  <button onClick={() => handleApproveReg(selectedReg)}
                    style={{ minWidth:'130px', padding:'11px 18px', background:'#16a34a', color:'white', border:'1px solid #16a34a', borderRadius:'10px', fontSize:'13px', fontWeight:'600', cursor:'pointer', boxShadow:'0 6px 14px rgba(22,163,74,.18)' }}>
                    <i className="ti ti-check" /> อนุมัติ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal เพิ่ม/แก้ไขพนักงาน */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '0.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a' }}>{editData ? 'แก้ไขข้อมูลพนักงาน' : 'เพิ่มพนักงานใหม่'}</p>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {error && (
                <div style={{ background: '#fef2f2', border: '0.5px solid #fecaca', color: '#ef4444', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
              )}
              {[
                { title: 'ข้อมูลส่วนตัว', fields: [
                  { label: 'เลขบัตรประชาชน *', key: 'id_card', type: 'text', colSpan: 2, disabled: !!editData, maxLength: 13 },
                  { label: 'คำนำหน้า', key: 'prefix', type: 'select', options: ['นาย', 'นาง', 'นางสาว'] },
                  { label: 'วันเกิด *', key: 'birth_date', type: 'date' },
                  { label: 'ชื่อ *', key: 'first_name', type: 'text' },
                  { label: 'นามสกุล *', key: 'last_name', type: 'text' },
                  { label: 'โทรศัพท์', key: 'phone', type: 'text' },
                  { label: 'Email', key: 'email', type: 'email' },
                ]},
                { title: 'ที่อยู่', fields: [
                  { label: 'บ้านเลขที่', key: 'house_no', type: 'text' },
                  { label: 'หมู่ที่', key: 'village_no', type: 'text' },
                  { label: 'ถนน', key: 'road', type: 'text', colSpan: 2 },
                  { label: 'ตำบล/แขวง', key: 'subdistrict', type: 'text' },
                  { label: 'อำเภอ/เขต', key: 'district', type: 'text' },
                  { label: 'จังหวัด', key: 'province', type: 'text' },
                  { label: 'รหัสไปรษณีย์', key: 'zipcode', type: 'text' },
                ]},
                { title: 'ข้อมูลการทำงาน', fields: [
                  { label: 'กองงาน *', key: 'division_id', type: 'select', options: divisions.map(d => ({ value: d.id, label: d.name })), colSpan: 2 },
                  { label: 'ตำแหน่ง', key: 'position', type: 'text' },
                  ...(editData ? [{label:'เงินเดือน *',key:'salary',type:'number'},{label:'หมายเหตุในการแก้ไข *',key:'note',type:'text',colSpan:2}] : []),
                  { label: 'ประเภทพนักงาน', key: 'emp_type', type: 'select', options: ['ทั่วไป', 'ตามภารกิจ', 'ผู้เชี่ยวชาญพิเศษ'] },
                ]},
              ].map((section, si) => (
                <div key={si} style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '500', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '12px' }}>{section.title}</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {section.fields.map(f => (
                      <div key={f.key} style={{ gridColumn: f.colSpan === 2 ? 'span 2' : 'span 1' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '5px' }}>{f.label}</label>
                        {f.type === 'select' ? (
                          <select disabled={!!editData && !['division_id','position','salary','note'].includes(f.key)} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} style={inputStyle}>
                            {f.key === 'division_id' && <option value="">เลือกกองงาน</option>}
                            {f.options?.map(opt => typeof opt === 'string'
                              ? <option key={opt} value={opt}>{opt}</option>
                              : <option key={opt.value} value={opt.value}>{opt.label}</option>
                            )}
                          </select>
                        ) : (
                          <input type={f.type} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                            disabled={f.disabled || (!!editData && !['division_id','position','salary','note'].includes(f.key))} maxLength={f.maxLength}
                            style={{ ...inputStyle, background: f.disabled ? '#f8fafc' : 'white' }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button onClick={() => setShowModal(false)}
                  style={{ flex: 1, padding: '10px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}>
                  ยกเลิก
                </button>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex: 1, padding: '10px', background: saving ? '#a5b4fc' : '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
